import { useState, useCallback } from 'react';
import type { UseFormReturn, FieldPath, FieldValues } from 'react-hook-form';

const TYPO_MAP: Record<string, string> = {
  'gimail.com': 'gmail.com',
  'gimali.com': 'gmail.com',
  'gamail.com': 'gmail.com',
  'gmaijl.com': 'gmail.com',
  'gmail.con': 'gmail.com',
  'gmail.co': 'gmail.com',
  'gnail.com': 'gmail.com',
  'gmal.com': 'gmail.com',
  'gmial.com': 'gmail.com',
  'gmai.com': 'gmail.com',
  'gamil.com': 'gmail.com',
  'yahoko.com': 'yahoo.com',
  'yaho.com': 'yahoo.com',
  'yahooo.com': 'yahoo.com',
  'yhaoo.com': 'yahoo.com',
};

export function useEmailTypoDetection<
  TFieldValues extends FieldValues = FieldValues,
>(form: UseFormReturn<TFieldValues>, fieldName: FieldPath<TFieldValues>) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [isEmailRoDomain, setIsEmailRoDomain] = useState(false);

  const checkEmail = useCallback(
    (email: string) => {
      if (!email || !email.includes('@')) {
        setSuggestion(null);
        setIsEmailRoDomain(false);
        return;
      }

      const domain = email.split('@')[1]?.toLowerCase();
      if (!domain) {
        setSuggestion(null);
        setIsEmailRoDomain(false);
        return;
      }

      // Check for @email.ro domain
      setIsEmailRoDomain(domain === 'email.ro');

      // Check for typos
      const correctDomain = TYPO_MAP[domain];
      if (correctDomain) {
        const localPart = email.split('@')[0];
        setSuggestion(`${localPart}@${correctDomain}`);
      } else {
        setSuggestion(null);
      }
    },
    []
  );

  const applySuggestion = useCallback(() => {
    if (suggestion) {
      form.setValue(fieldName, suggestion as any, { shouldValidate: true });
      setSuggestion(null);
    }
  }, [form, fieldName, suggestion]);

  const dismissSuggestion = useCallback(() => {
    setSuggestion(null);
  }, []);

  return { suggestion, applySuggestion, dismissSuggestion, isEmailRoDomain, checkEmail };
}
